import { CourseOverviewEditor } from './CourseOverviewEditor';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type BuilderCourse, type BuilderUnitRow, type CertificateTemplateRow, type CompletionRule, type ScoreRule, type UnitType } from '../../api';

const TYPE_LABEL: Record<string, string> = {
  content: 'Lesson', craft: 'Interactive lesson', video: 'Video', audio: 'Audio',
  document: 'Document', webpage: 'Web page', test: 'Test', survey: 'Survey',
  assignment: 'Assignment', ilt: 'Live session', section: 'Section heading',
};

/** Unit formats supported by the course builder. */
const ADDABLE: { type: UnitType; label: string; hint: string }[] = [
  { type: 'content', label: 'Lesson', hint: 'Written material the trainee reads' },
  { type: 'craft', label: 'Interactive lesson', hint: 'A lesson built from reusable content blocks' },
  { type: 'video', label: 'Video', hint: 'A file you upload, or a YouTube link' },
  { type: 'audio', label: 'Audio', hint: 'An audio recording for the trainee' },
  { type: 'ilt', label: 'Live session', hint: 'Schedule classes and record attendance' },
  { type: 'document', label: 'Document', hint: 'A PDF or Word file to read' },
  { type: 'test', label: 'Test', hint: 'Questions with a pass mark' },
  { type: 'survey', label: 'Survey', hint: 'Questions with no right answer' },
  { type: 'assignment', label: 'Assignment', hint: 'Work an instructor marks' },
  { type: 'webpage', label: 'Web page', hint: 'An external page shown inside the course' },
  { type: 'section', label: 'Section heading', hint: 'Splits the list into parts' },
];

type AuthoringCourse = BuilderCourse & { capabilities?: { settings: boolean; createUnits: boolean; updateUnits: boolean; deleteUnits: boolean } };

export function CourseEditor({ mode = 'admin' }: { mode?: 'admin' | 'instructor' }) {
  const instructor = mode === 'instructor';
  const base = instructor ? '/instructor/courses' : '/admin/courses';
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<AuthoringCourse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get<AuthoringCourse>(`/admin/builder/${id}/content`)); }
    catch (e) { setError((e as ApiError).message); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  if (error && !data) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;
  const { course, units } = data;
  const caps = data.capabilities ?? { settings: false, createUnits: false, updateUnits: false, deleteUnits: false };

  const say = (m: string) => { setNote(m); setError(null); };

  const move = async (index: number, delta: number) => {
    const next = units.map((u) => u.id);
    const to = index + delta;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to], next[index]];
    setBusy(true);
    try { await api.post(`/admin/builder/${course.id}/units/order`, { unitIds: next }); await load(); }
    catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const remove = async (u: BuilderUnitRow) => {
    const warn = u.learnersStarted > 0
      ? `${u.learnersStarted} ${u.learnersStarted === 1 ? 'person has' : 'people have'} worked on "${u.name}". Deleting it erases their progress permanently.\n\nSwitching the unit off hides it and keeps the records. Delete anyway?`
      : `Delete "${u.name}"? Nobody has opened it yet.`;
    if (!confirm(warn)) return;
    setBusy(true);
    try {
      await api.del(`/admin/builder/${course.id}/units/${u.id}${u.learnersStarted > 0 ? '?force=true' : ''}`);
      say(`"${u.name}" deleted.`); await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const toggle = async (u: BuilderUnitRow) => {
    setBusy(true);
    try {
      await api.patch(`/admin/builder/${course.id}/units/${u.id}`, { isActive: !u.isActive });
      await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const setCourseStatus = async (status: 'active' | 'inactive') => {
    if (status === 'active') {
      const empty = units.filter((u) => u.type !== 'section' && !u.isActive).length;
      const live = units.filter((u) => u.type !== 'section' && u.isActive).length;
      if (live === 0) { setError('Switch at least one unit on first — an empty course shows trainees nothing.'); return; }
      if (empty > 0 && !confirm(`${empty} unit${empty === 1 ? '' : 's'} are still switched off and will not appear. Enable course access anyway?`)) return;
    }
    setBusy(true);
    try {
      await api.patch(instructor ? `/admin/builder/${course.id}/settings` : `/admin/courses/${course.id}`, { status });
      say(status === 'active' ? 'Course access enabled. Learners see published content from enabled units.' : 'Course access disabled.');
      await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <nav className="crumbs">
        <Link to={base}>Courses</Link> <span aria-hidden>›</span> {course.name}
      </nav>

      <div className="page-head">
        <h1 className="h1">
          {course.name}
          {course.status !== 'active' && <span className="tag">switched off</span>}
        </h1>
        <div className="row">
          {!instructor && <Link className="btn btn--ghost" to={`/admin/courses/${course.id}`}>Who is on it ({data.enrolledCount})</Link>}
          {caps.settings && (course.status === 'active'
            ? <button className="btn btn--ghost" disabled={busy} onClick={() => void setCourseStatus('inactive')}>Disable course access</button>
            : <button className="btn btn--primary" disabled={busy} onClick={() => void setCourseStatus('active')}>Enable course access</button>)}
        </div>
      </div>

      {note && <p className="notice">{note}</p>}
      {error && <p className="auth__error">{error}</p>}

      {caps.settings && <CourseOverviewEditor key={`overview-${course.id}`} courseId={course.id} liveOverview={course} onStateChanged={() => void load()} />}

      {caps.settings && <CourseSettings key={`settings-${course.id}`} data={data} instructor={instructor} onSaved={(m) => { say(m); void load(); }} onError={setError} />}

      <div className="page-head">
        <h2 className="h2">Content ({units.filter((u) => u.type !== 'section').length} units)</h2>
        {caps.createUnits && <button className="btn btn--primary" onClick={() => setAdding(!adding)}>{adding ? 'Cancel' : 'Add a unit'}</button>}
      </div>

      <p className="muted">Learning and access settings, course and unit visibility, unit order, removal, and assessment question changes apply immediately when saved. Each interactive lesson draft is published separately.</p>

      {adding && (
        <div className="panel">
          <p className="muted small">New units start switched off, so a half-written lesson never appears to a trainee.</p>
          <div className="addgrid">
            {ADDABLE.map((a) => (
              <button key={a.type} className="addcard" disabled={busy} onClick={async () => {
                const name = prompt(`Name for the new ${a.label.toLowerCase()}`);
                if (!name?.trim()) return;
                setBusy(true);
                try {
                  const r = await api.post<{ unit: { id: number } }>(`/admin/builder/${course.id}/units`, { type: a.type, name: name.trim() });
                  setAdding(false);
                  navigate(`${base}/${course.id}/edit/units/${r.unit.id}`);
                } catch (e) { setError((e as ApiError).message); }
                finally { setBusy(false); }
              }}>
                <strong>{a.label}</strong>
                <span className="muted small">{a.hint}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {units.length === 0 && <p className="muted">Nothing in this course yet.</p>}

      <ol className="builder">
        {units.map((u, i) => (
          <li key={u.id} className={`builder__row ${u.type === 'section' ? 'is-section' : ''} ${!u.isActive ? 'is-off' : ''}`}>
            <span className="builder__moves">
              <button type="button" disabled={!caps.updateUnits || busy || i === 0} onClick={() => void move(i, -1)} aria-label={`Move ${u.name} up`}>↑</button>
              <button type="button" disabled={!caps.updateUnits || busy || i === units.length - 1} onClick={() => void move(i, 1)} aria-label={`Move ${u.name} down`}>↓</button>
            </span>

            <span className="builder__main">
              <Link to={`${base}/${course.id}/edit/units/${u.id}`}>{u.name}</Link>
              <span className="muted small">
                {TYPE_LABEL[u.type] ?? u.type}
                {u.type === 'test' || u.type === 'survey' ? ` · ${u.questionCount} question${u.questionCount === 1 ? '' : 's'}` : ''}
                {u.learnersStarted > 0 ? ` · ${u.learnersStarted} started` : ''}
              </span>
            </span>

            <label className="builder__toggle">
              <input type="checkbox" checked={u.isActive} disabled={!caps.updateUnits || busy} onChange={() => void toggle(u)} />
              <span>{u.isActive ? 'on' : 'off'}</span>
            </label>

            <button className="link-danger" disabled={!caps.deleteUnits || busy} onClick={() => void remove(u)}>Delete</button>
          </li>
        ))}
      </ol>
    </>
  );
}

/**
 * Course settings.
 *
 * All of this arrived from TalentLMS and the engine has enforced it ever
 * since; until now nobody could change any of it. The rules that can make a
 * course impossible to finish — a completion rule naming units from another
 * course, a weighting that adds to nothing, a prerequisite that loops back —
 * are checked by the server and refused with a reason, so this form can stay
 * simple and just show what came back.
 */
function CourseSettings({ data, instructor, onSaved, onError }: {
  instructor: boolean;
  data: BuilderCourse;
  onSaved: (m: string) => void;
  onError: (m: string) => void;
}) {
  const { course, units, prerequisiteIds, otherCourses } = data;
  const completable = units.filter((u) => u.type !== 'section');
  const tests = units.filter((u) => u.type === 'test');

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    traversal: course.traversal,
    timeLimitDays: course.timeLimitDays == null ? '' : String(course.timeLimitDays),
    capacity: course.capacity == null ? '' : String(course.capacity),
    isHiddenFromCatalog: course.isHiddenFromCatalog,
    isContentLocked: course.isContentLocked,
    enrollmentApproval: course.enrollmentApproval,
    retainAccessAfterCompletion: course.retainAccessAfterCompletion,
    certificateTemplateId: course.certificateTemplateId == null ? '' : String(course.certificateTemplateId),
    certificateValidDays: course.certificateValidDays == null ? '' : String(course.certificateValidDays),
  });
  const [completion, setCompletion] = useState<CompletionRule>(course.completionRule);
  const [score, setScore] = useState<ScoreRule>(course.scoreRule);
  const [prereqs, setPrereqs] = useState<number[]>(prerequisiteIds);
  const [templates, setTemplates] = useState<CertificateTemplateRow[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || instructor) return;
    void (async () => {
      try { setTemplates((await api.get<{ templates: CertificateTemplateRow[] }>('/admin/certificates/templates')).templates); }
      catch { /* the rest of the settings still work without the list */ }
    })();
  }, [open, instructor]);

  if (!open) {
    return <p><button className="btn btn--ghost" onClick={() => setOpen(true)}>Learning and access settings</button></p>;
  }

  const toggleUnitInRule = (unitId: number) => {
    if (completion.type !== 'units') return;
    const has = completion.unitIds.includes(unitId);
    setCompletion({
      type: 'units',
      unitIds: has ? completion.unitIds.filter((x) => x !== unitId) : [...completion.unitIds, unitId],
    });
  };

  return (
    <section className="panel">
      <h2 className="h2">Learning and access settings</h2>
      <p className="muted">These rules apply immediately when saved. Course overview drafts and interactive lesson drafts are published separately.</p>
      <div className="form">
        <h3 className="h3">Working through it</h3>

        <label><span>Order</span>
          <select value={form.traversal} onChange={(e) => setForm({ ...form, traversal: e.target.value })}>
            <option value="sequential">In order — each unit unlocks the next</option>
            <option value="free">Any order</option>
          </select></label>

        <label><span>Counts as finished when</span>
          <select value={completion.type} onChange={(e) => {
            const t = e.target.value;
            setCompletion(
              t === 'all_units' ? { type: 'all_units' }
              : t === 'percentage' ? { type: 'percentage', value: 80 }
              : t === 'test' ? { type: 'test', unitId: tests[0]?.id ?? 0 }
              : { type: 'units', unitIds: [] },
            );
          }}>
            <option value="all_units">Every unit is finished</option>
            <option value="percentage">A percentage of the units is finished</option>
            <option value="units">Particular units are finished</option>
            <option value="test">One test is passed</option>
          </select></label>

        {completion.type === 'percentage' && (
          <label><span>Percentage</span>
            <input type="number" min={1} max={100} className="narrow" value={completion.value}
              onChange={(e) => setCompletion({ type: 'percentage', value: Number(e.target.value) })} /></label>
        )}

        {completion.type === 'units' && (
          <label><span>Which units</span>
            <span className="checklist">
              {completable.map((u) => (
                <label key={u.id} className="check">
                  <input type="checkbox" checked={completion.unitIds.includes(u.id)}
                    onChange={() => toggleUnitInRule(u.id)} />
                  {u.name}
                </label>
              ))}
              {completable.length === 0 && <em className="muted">This course has no units yet.</em>}
            </span></label>
        )}

        {completion.type === 'test' && (
          <label><span>Which test</span>
            <select value={String(completion.unitId)}
              onChange={(e) => setCompletion({ type: 'test', unitId: Number(e.target.value) })}>
              {tests.length === 0 && <option value="0">This course has no tests</option>}
              {tests.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
            </select></label>
        )}

        <label><span>Score comes from</span>
          <select value={score.type} onChange={(e) => {
            const t = e.target.value;
            setScore(
              t === 'tests_only' ? { type: 'tests_only' }
              : t === 'tests_and_assignments' ? { type: 'tests_and_assignments' }
              : { type: 'weighted', weights: Object.fromEntries(completable.map((u) => [String(u.id), 1])) },
            );
          }}>
            <option value="tests_only">Tests only</option>
            <option value="tests_and_assignments">Tests and assignments</option>
            <option value="weighted">Units I weight myself</option>
          </select></label>

        {score.type === 'weighted' && (
          <label><span>Weights</span>
            <span className="checklist">
              {completable.map((u) => (
                <span key={u.id} className="weightrow">
                  <input type="number" min={0} max={100} className="narrow"
                    value={score.weights[String(u.id)] ?? 0}
                    onChange={(e) => setScore({
                      type: 'weighted',
                      weights: { ...score.weights, [String(u.id)]: Number(e.target.value) },
                    })} />
                  {u.name}
                </span>
              ))}
              <em className="muted small">A unit weighing nothing counts for nothing. At least one has to weigh something.</em>
            </span></label>
        )}

        <label><span>Finish within</span>
          <span className="withhint">
            <input type="number" min={0} max={3650} className="narrow" value={form.timeLimitDays}
              onChange={(e) => setForm({ ...form, timeLimitDays: e.target.value })} />
            <em>days of being enrolled — leave empty for no limit</em>
          </span></label>

        {!instructor && <>
        <label><span>Open only after</span>
          <span className="checklist">
            {otherCourses.map((c) => (
              <label key={c.id} className="check">
                <input type="checkbox" checked={prereqs.includes(c.id)}
                  onChange={() => setPrereqs(prereqs.includes(c.id)
                    ? prereqs.filter((x) => x !== c.id) : [...prereqs, c.id])} />
                {c.name}
              </label>
            ))}
            {otherCourses.length === 0 && <em className="muted">There are no other courses yet.</em>}
          </span></label>

        <h3 className="h3">Who can get on it</h3>

        <label><span>Capacity</span>
          <span className="withhint">
            <input type="number" min={1} className="narrow" value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
            <em>places — leave empty for no limit. It cannot be set below the {data.enrolledCount} already on it.</em>
          </span></label>

        <label className="check">
          <input type="checkbox" checked={form.isHiddenFromCatalog}
            onChange={(e) => setForm({ ...form, isHiddenFromCatalog: e.target.checked })} />
          Hide it from the catalogue — people can only be put on it by an administrator
        </label>
        <label className="check">
          <input type="checkbox" checked={form.enrollmentApproval}
            onChange={(e) => setForm({ ...form, enrollmentApproval: e.target.checked })} />
          Somebody has to approve each request to join
        </label>
        </>}
        <label className="check">
          <input type="checkbox" checked={form.isContentLocked}
            onChange={(e) => setForm({ ...form, isContentLocked: e.target.checked })} />
          Hide learner download controls
        </label>
        <label className="check">
          <input type="checkbox" checked={form.retainAccessAfterCompletion}
            onChange={(e) => setForm({ ...form, retainAccessAfterCompletion: e.target.checked })} />
          Keep access after finishing, so they can revise
        </label>

        {!instructor && <>
        <h3 className="h3">Certificate</h3>

        <label><span>Template</span>
          <select value={form.certificateTemplateId}
            onChange={(e) => setForm({ ...form, certificateTemplateId: e.target.value })}>
            <option value="">None — this course awards no certificate</option>
            {templates.map((t) => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
          </select></label>
        <label><span>Valid for</span>
          <input type="number" min={0} max={3650} placeholder="days — leave empty for no expiry"
            value={form.certificateValidDays}
            onChange={(e) => setForm({ ...form, certificateValidDays: e.target.value })} /></label>

        </>}
        <div className="row">
          <button className="btn btn--primary" disabled={busy} onClick={async () => {
            setBusy(true);
            try {
              await api.patch(instructor ? `/admin/builder/${course.id}/settings` : `/admin/courses/${course.id}`, {
                traversal: form.traversal,
                timeLimitDays: form.timeLimitDays ? Number(form.timeLimitDays) : null,
                ...(!instructor ? { capacity: form.capacity ? Number(form.capacity) : null,
                isHiddenFromCatalog: form.isHiddenFromCatalog, enrollmentApproval: form.enrollmentApproval } : {}),
                isContentLocked: form.isContentLocked,
                retainAccessAfterCompletion: form.retainAccessAfterCompletion,
                completionRule: completion,
                scoreRule: score,
                ...(!instructor ? { prerequisiteIds: prereqs,
                certificateTemplateId: form.certificateTemplateId ? Number(form.certificateTemplateId) : null,
                certificateValidDays: form.certificateValidDays ? Number(form.certificateValidDays) : null } : {}),
              });
              setOpen(false); onSaved('Learning and access settings applied.');
            } catch (e) { onError((e as ApiError).message); }
            finally { setBusy(false); }
          }}>Save settings</button>
          <button className="btn btn--ghost" onClick={() => setOpen(false)}>Cancel</button>
        </div>
      </div>
    </section>
  );
}
